import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Check, X, BookOpen, Star, Printer } from "lucide-react";
import { API_BASE } from "../config";
import "../styles/resultDetailPage.css";
import apiFetch from "../apiFetch";

const TYPE_LABELS = {
  comprehension:      "Comprehension",
  mcq:                "MCQ Master",
  ranked:             "Ranked",
  book_comprehension: "Book Comprehension",
};

function formatDate(ts) {
  return new Date(ts).toLocaleString("en-GB", {
    day: "numeric", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}

function ScoreBar({ score, maxScore }) {
  const pct = maxScore > 0 ? Math.round((score / maxScore) * 100) : 0;
  const cls = pct >= 70 ? "good" : pct >= 40 ? "mid" : "low";
  return (
    <div className="rd-score-bar-wrap">
      <div className="rd-score-bar">
        <div className={`rd-score-fill ${cls}`} style={{ width: `${pct}%` }} />
      </div>
      <span className={`rd-score-label ${cls}`}>{score}/{maxScore} ({pct}%)</span>
    </div>
  );
}

/* ── Comprehension / Book Written ── */
function WrittenResults({ questions, userAnswers, markingResults }) {
  const results = markingResults?.results || [];
  return (
    <div className="rd-questions">
      {questions.map((q, i) => {
        const marked = results[i] || {};
        const awarded = marked.marksAwarded ?? "—";
        const total = q.marks ?? "?";
        const correct = typeof marked.marksAwarded === "number" && marked.marksAwarded >= total;
        return (
          <div key={i} className={`rd-question ${correct ? "correct" : "partial"}`}>
            <div className="rd-q-header">
              <span className="rd-q-num">Q{i + 1}</span>
              <span className="rd-q-marks">{awarded}/{total} marks</span>
            </div>
            <p className="rd-q-text">{q.question}</p>
            <div className="rd-answer-block">
              <span className="rd-answer-label">Your answer</span>
              <p className="rd-answer-text">{userAnswers[i] || <em>No answer</em>}</p>
            </div>
            {marked.explanation && (
              <div className="rd-feedback">
                <span className="rd-feedback-label">Feedback</span>
                <p>{marked.explanation}</p>
              </div>
            )}
            {marked.feedback && marked.feedback !== marked.explanation && (
              <div className="rd-feedback">
                <p>{marked.feedback}</p>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

/* ── MCQ questions (MCQ Master + Ranked MCQ section) ── */
function MCQResults({ questions, userAnswers }) {
  return (
    <div className="rd-questions">
      {questions.map((q, i) => {
        const ua = userAnswers[i];
        const correct = ua === q.correct;

        // options can be an object {A:"text",...} or an array ["text",...]
        const optEntries = Array.isArray(q.options)
          ? q.options.map((text, j) => [String(j), text])
          : Object.entries(q.options || {});

        return (
          <div key={i} className={`rd-question ${correct ? "correct" : "wrong"}`}>
            <div className="rd-q-header">
              <span className="rd-q-num">Q{i + 1}</span>
              {correct
                ? <span className="rd-q-result correct"><Check size={13} aria-hidden="true" /> Correct</span>
                : <span className="rd-q-result wrong"><X size={13} aria-hidden="true" /> Wrong</span>}
            </div>
            <p className="rd-q-text">{q.question}</p>
            <div className="rd-options">
              {optEntries.map(([key, text]) => {
                const isCorrect = key === q.correct;
                const isUser = key === ua;
                return (
                  <div
                    key={key}
                    className={`rd-option ${isCorrect ? "correct" : ""} ${isUser && !isCorrect ? "wrong" : ""}`}
                  >
                    {isCorrect && <><Check size={13} aria-hidden="true" /><span className="sr-only">Correct answer: </span></>}
                    {isUser && !isCorrect && <><X size={13} aria-hidden="true" /><span className="sr-only">Your wrong answer: </span></>}
                    <span className="rd-option-key">{key}</span> {text}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ── Ranked: MCQ + Written sections ── */
function RankedResults({ data }) {
  const { mcqQuestions = [], writtenQuestions = [], mcqAnswers = {}, writtenAnswers = {}, markingResults } = data;
  const mcqResults = markingResults?.mcqResults || [];
  const writtenResults = markingResults?.writtenResults || markingResults?.results || [];

  return (
    <>
      {mcqQuestions.length > 0 && (
        <section className="rd-section">
          <h3 className="rd-section-title">Multiple Choice</h3>
          <MCQResults questions={mcqQuestions} userAnswers={mcqAnswers} mcqResults={mcqResults} />
        </section>
      )}
      {writtenQuestions.length > 0 && (
        <section className="rd-section">
          <h3 className="rd-section-title">Written</h3>
          <WrittenResults
            questions={writtenQuestions}
            userAnswers={writtenAnswers}
            markingResults={{ results: writtenResults }}
          />
        </section>
      )}
    </>
  );
}

export default function ResultDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => { document.title = "Result Detail — Owllet Reading Quest"; }, []);

  useEffect(() => {
    apiFetch(`/api/results/detail/${id}`)
      .then((r) => { if (!r.ok) throw new Error("Not found"); return r.json(); })
      .then((d) => { setResult(d); setLoading(false); })
      .catch(() => { setError("Result not found."); setLoading(false); });
  }, [id]);

  if (loading) return <div className="rd-loading">Loading result…</div>;
  if (error)   return <div className="rd-loading">{error}</div>;

  const { game_type, title, score, max_score, played_at, result_json: data } = result;

  const handlePrint = () => {
    const pct = max_score > 0 ? Math.round((score / max_score) * 100) : 0;
    const typeLabel = TYPE_LABELS[game_type] || game_type;

    const passageHtml = data?.passage
      ? `<h2>Passage</h2><div class="passage">${data.passage}</div>`
      : "";

    const writtenRows = (questions, userAnswers, markingResults) => {
      const results = markingResults?.results || [];
      return questions.map((q, i) => {
        const marked = results[i] || {};
        return `
          <div class="q-block">
            <div class="q-header">
              <span class="q-num">Q${i + 1}</span>
              <span class="q-marks">${marked.marksAwarded ?? "—"} / ${q.marks ?? "?"} marks</span>
            </div>
            <p class="q-text">${q.question}</p>
            <div class="section">
              <div class="section-label">Your Answer</div>
              <div class="answer-box">${userAnswers[i] || "<em>No answer</em>"}</div>
            </div>
            ${marked.explanation ? `<div class="section"><div class="section-label">Feedback</div><p>${marked.explanation}</p></div>` : ""}
          </div>`;
      }).join("");
    };

    const mcqRows = (questions, userAnswers) => {
      const optEntries = (opts) => Array.isArray(opts)
        ? opts.map((t, j) => [String(j), t])
        : Object.entries(opts || {});
      return questions.map((q, i) => {
        const ua = userAnswers[i];
        const correct = ua === q.correct;
        const opts = optEntries(q.options).map(([key, text]) => {
          const isCorrect = key === q.correct;
          const isUser = key === ua;
          const bg = isCorrect ? "#dcfce7" : isUser ? "#fee2e2" : "#f9fafb";
          const border = isCorrect ? "#86efac" : isUser ? "#fca5a5" : "#e5e7eb";
          return `<div class="opt" style="background:${bg};border-color:${border}"><strong>${key}</strong> ${text}</div>`;
        }).join("");
        return `
          <div class="q-block" style="border-color:${correct ? "#86efac" : "#fca5a5"};background:${correct ? "#f0fdf4" : "#fef2f2"}">
            <div class="q-header">
              <span class="q-num">Q${i + 1}</span>
              <span style="font-weight:700;color:${correct ? "#16a34a" : "#dc2626"}">${correct ? "✓ Correct" : "✗ Wrong"}</span>
            </div>
            <p class="q-text">${q.question}</p>
            <div class="opts">${opts}</div>
          </div>`;
      }).join("");
    };

    let questionsHtml = "";
    if (game_type === "comprehension") {
      questionsHtml = `<h2>Questions</h2>${writtenRows(data?.questions || [], data?.userAnswers || {}, data?.markingResults)}`;
    } else if (game_type === "mcq") {
      questionsHtml = `<h2>Questions</h2>${mcqRows(data?.questions || [], data?.userAnswers || {})}`;
    } else if (game_type === "ranked") {
      const mcqSection = data?.mcqQuestions?.length
        ? `<h2>Multiple Choice</h2>${mcqRows(data.mcqQuestions, data.mcqAnswers || {})}`
        : "";
      const writtenSection = data?.writtenQuestions?.length
        ? `<h2>Written</h2>${writtenRows(data.writtenQuestions, data.writtenAnswers || {}, { results: data.markingResults?.writtenResults || data.markingResults?.results || [] })}`
        : "";
      questionsHtml = mcqSection + writtenSection;
    } else if (game_type === "book_comprehension") {
      questionsHtml = data?.type === "mcq"
        ? `<h2>Questions</h2>${mcqRows(data?.questions || [], data?.userAnswers || {})}`
        : `<h2>Questions</h2>${writtenRows(data?.questions || [], data?.userAnswers || {}, data?.markingResults)}`;
    }

    const html = `<!DOCTYPE html><html><head>
      <meta charset="utf-8"/>
      <title>${title}</title>
      <style>
        body { font-family: Georgia, serif; color: #1f2937; margin: 0; padding: 32px; font-size: 13px; }
        h1 { font-size: 20px; margin: 0 0 4px; }
        .meta { font-size: 12px; color: #6b7280; margin-bottom: 6px; }
        .score { font-size: 13px; font-weight: 700; margin-bottom: 24px; }
        h2 { font-size: 15px; margin: 24px 0 10px; color: #374151; border-bottom: 1px solid #e5e7eb; padding-bottom: 6px; }
        .passage { background: #f9fafb; border-left: 3px solid #6366f1; padding: 14px 16px; border-radius: 4px; line-height: 1.7; margin-bottom: 8px; white-space: pre-wrap; }
        .q-block { border: 1px solid #e5e7eb; border-radius: 6px; padding: 16px; margin-bottom: 12px; break-inside: avoid; }
        .q-header { display: flex; justify-content: space-between; margin-bottom: 8px; }
        .q-num { font-weight: 700; color: #4f46e5; }
        .q-text { font-size: 13px; margin: 0 0 10px; line-height: 1.5; }
        .section { margin-top: 8px; }
        .section-label { font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px; color: #9ca3af; margin-bottom: 4px; }
        .answer-box { background: #f3f4f6; border-radius: 4px; padding: 8px 12px; line-height: 1.5; white-space: pre-wrap; }
        .opts { display: flex; flex-direction: column; gap: 6px; }
        .opt { padding: 6px 10px; border-radius: 4px; border: 1px solid #e5e7eb; font-size: 13px; }
        @media print { body { padding: 16px; } }
      </style>
    </head><body>
      <h1>${typeLabel} — ${title}</h1>
      <p class="meta">${formatDate(played_at)}</p>
      <p class="score">Score: ${score} / ${max_score} (${pct}%)</p>
      ${passageHtml}
      ${questionsHtml}
    </body></html>`;

    const win = window.open("", "_blank");
    win.document.write(html);
    win.document.close();
    win.document.title = title;
    win.focus();
    setTimeout(() => win.print(), 400);
  };

  return (
    <div className="rd-page">
      {/* Header */}
      <div className="rd-header">
        <button className="rd-back-btn" onClick={() => navigate("/my-results")}>
          <ArrowLeft size={16} aria-hidden="true" /> Back
        </button>
        <div className="rd-header-center">
          <BookOpen size={20} aria-hidden="true" />
          <h1>{TYPE_LABELS[game_type] || game_type}</h1>
        </div>
        <button className="rd-back-btn" onClick={handlePrint} aria-label="Print result or save as PDF">
          <Printer size={16} aria-hidden="true" />
        </button>
      </div>

      <div className="rd-body">
        {/* Summary card */}
        <div className="rd-summary">
          <h2 className="rd-summary-title">{title}</h2>
          <p className="rd-summary-date">{formatDate(played_at)}</p>
          <div className="rd-summary-score">
            <Star size={16} aria-hidden="true" />
            <span>Score</span>
          </div>
          <ScoreBar score={score} maxScore={max_score} />
        </div>

        {/* Passage (comprehension / MCQ) */}
        {data?.passage && (
          <div className="rd-passage">
            <h3 className="rd-section-title">Passage</h3>
            <p>{data.passage}</p>
          </div>
        )}

        {/* Questions by game type */}
        {game_type === "comprehension" && data && (
          <WrittenResults
            questions={data.questions || []}
            userAnswers={data.userAnswers || {}}
            markingResults={data.markingResults}
          />
        )}

        {game_type === "mcq" && data && (
          <MCQResults
            questions={data.questions || []}
            userAnswers={data.userAnswers || {}}
          />
        )}

        {game_type === "ranked" && data && (
          <RankedResults data={data} />
        )}

        {game_type === "book_comprehension" && data && (
          data.type === "mcq" ? (
            <MCQResults
              questions={data.questions || []}
              userAnswers={data.userAnswers || {}}
            />
          ) : (
            <WrittenResults
              questions={data.questions || []}
              userAnswers={data.userAnswers || {}}
              markingResults={data.markingResults}
            />
          )
        )}
      </div>
    </div>
  );
}
